/**
 * Facebook Pixel Provider
 * Handles Facebook Pixel event tracking
 * Uses ad-blocker-resistant naming
 */

import { createDebugLogger } from '../../../utils/debugLogger';

const logger = createDebugLogger('facebook');

interface FacebookPixelConfig {
    pixelId: string;
}

/**
 * Facebook Pixel event mapping
 * Maps standard event names to Facebook Pixel events
 */
const FACEBOOK_EVENT_MAP: Record<string, string> = {
    signup_started: 'InitiateCheckout',
    signup_step_viewed: 'ViewContent',
    signup_step_completed: 'AddToCart',
    product_selected: 'AddToCart',
    payment_method_created: 'AddPaymentInfo',
    signup_completed: 'CompleteRegistration',
    conversion_completed: 'Purchase',
    payment_method_failed: 'AddPaymentInfo',
    error_occurred: 'PageView',
    button_clicked: 'Lead',
    form_field_completed: 'Lead',
};

/**
 * Initialize Facebook Pixel
 */
export const initializeFacebookPixel = (config: FacebookPixelConfig): void => {
    if (typeof window === 'undefined') {
        return;
    }

    // Check if already initialized
    if ((window as any).fbq) {
        return;
    }

    // Load Facebook Pixel script
    (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function(...args: any[]) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, prefer-spread -- verbatim Facebook Pixel bootstrap snippet
            n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode?.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    // Initialize pixel
    (window as any).fbq('init', config.pixelId);
    (window as any).fbq('track', 'PageView');
};

/**
 * Track event with Facebook Pixel
 */
export const trackFacebookEvent = (
    eventName: string,
    properties: Record<string, any> = {}
): void => {
    logger.track(eventName, properties);
    if (typeof window === 'undefined' || !(window as any).fbq) {
        return;
    }

    try {
        // Map standard event name to Facebook event
        const facebookEvent = FACEBOOK_EVENT_MAP[eventName] || 'CustomEvent';

        // Transform properties for Facebook Pixel
        const facebookProperties: Record<string, any> = {};

        // Map common properties
        if (properties.value !== undefined) {
            facebookProperties.value = properties.value;
        }
        if (properties.currency) {
            facebookProperties.currency = properties.currency;
        } else {
            facebookProperties.currency = 'USD';
        }

        // For Purchase events, ensure all required fields are populated
        if (facebookEvent === 'Purchase') {
            // Required: value, currency
            facebookProperties.value = properties.value !== undefined ? properties.value : 0;
            facebookProperties.currency = properties.currency || 'USD';
            
            // Content properties - use items array if available
            if (properties.items && Array.isArray(properties.items) && properties.items.length > 0) {
                // Use first item for content_name
                facebookProperties.content_name = properties.items[0].item_name || properties.membership_tier || properties.product_name || '';
                
                // content_ids array - all product IDs
                facebookProperties.content_ids = properties.items.map((item: any) => item.item_id || item.id).filter((id: any) => id);
                
                // contents array - detailed product information (Facebook best practice)
                facebookProperties.contents = properties.items.map((item: any) => ({
                    id: item.item_id || item.id || properties.product_id || '',
                    quantity: item.quantity !== undefined ? item.quantity : 1,
                    item_price: item.price !== undefined ? item.price : (properties.value || 0),
                })).filter((content: any) => content.id);
            } else {
                // Fallback if items array not available
                facebookProperties.content_name = properties.membership_tier || properties.product_name || '';
                facebookProperties.content_ids = [properties.product_id || properties.price_id || ''];
                facebookProperties.contents = [{
                    id: properties.product_id || properties.price_id || '',
                    quantity: 1,
                    item_price: properties.value || 0,
                }];
            }
            
            // Content type
            facebookProperties.content_type = 'product';
            
            // Transaction ID
            if (properties.transaction_id) {
                facebookProperties.order_id = properties.transaction_id;
            }
        } else {
            // For non-purchase events, use simpler mapping
            if (properties.membership_tier) {
                facebookProperties.content_name = properties.membership_tier;
            }
            if (properties.product_name) {
                facebookProperties.content_name = properties.product_name;
            }
            if (properties.price_id) {
                facebookProperties.content_ids = [properties.price_id];
            }
            if (properties.step !== undefined) {
                facebookProperties.content_category = `step_${properties.step}`;
            }
        }

        // Track the event
        (window as any).fbq('track', facebookEvent, facebookProperties);
    } catch (error) {
        console.warn('Facebook Pixel tracking failed:', error);
    }
};

/**
 * Track page view with Facebook Pixel
 */
export const trackFacebookPageView = (properties: Record<string, any> = {}): void => {
    if (typeof window === 'undefined' || !(window as any).fbq) {
        return;
    }

    try {
        (window as any).fbq('track', 'PageView', properties);
    } catch (error) {
        console.warn('Facebook Pixel page view failed:', error);
    }
};

/**
 * Create Facebook Pixel plugin for analytics library
 */
export const createFacebookPixelPlugin = (config: FacebookPixelConfig) => {
    return {
        name: 'facebook-pixel',
        config,
        initialize: () => {
            initializeFacebookPixel(config);
        },
        track: ({ payload }: any) => {
            trackFacebookEvent(payload.event, payload.properties || {});
        },
        page: ({ payload }: any) => {
            trackFacebookPageView(payload.properties || {});
        },
    };
};

